'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────

interface AlertRule {
  id: string;
  name: string;
  description: string;
  supplier_slug: string | null;
  metric_name: string;
  condition_operator: string;
  threshold_value: number;
  severity: 'info' | 'warning' | 'critical' | 'emergency';
  enabled: boolean;
  cooldown_minutes: number;
  escalation_minutes: number;
  notification_channels: string[];
  created_at: string;
}

interface Alert {
  id: string;
  alert_key: string;
  supplier_slug: string | null;
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'critical' | 'emergency';
  status: 'active' | 'acknowledged' | 'resolved' | 'suppressed';
  triggered_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  escalation_level: number;
  metadata: Record<string, any>;
}

interface AlertSummary {
  total: number;
  active: number;
  critical: number;
  acknowledged: number;
  resolved: number;
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string; icon: string }> = {
  info: { label: 'Info', color: 'text-blue-400', bg: 'bg-blue-500/15', dot: 'bg-blue-400', icon: 'InformationCircleIcon' },
  warning: { label: 'Warning', color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-400', icon: 'ExclamationTriangleIcon' },
  critical: { label: 'Critical', color: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-400', icon: 'ExclamationCircleIcon' },
  emergency: { label: 'Emergency', color: 'text-rose-300', bg: 'bg-rose-500/20', dot: 'bg-rose-400', icon: 'FireIcon' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'text-red-400' },
  acknowledged: { label: 'Acknowledged', color: 'text-amber-400' },
  resolved: { label: 'Resolved', color: 'text-emerald-400' },
  suppressed: { label: 'Suppressed', color: 'text-muted-foreground' },
};

const TABS = [
  { id: 'dashboard', label: 'Alert Dashboard', icon: 'BellAlertIcon' },
  { id: 'rules', label: 'Alert Rules', icon: 'AdjustmentsHorizontalIcon' },
  { id: 'escalations', label: 'Escalations', icon: 'ArrowTrendingUpIcon' },
  { id: 'notifications', label: 'Notifications', icon: 'EnvelopeIcon' },
  { id: 'history', label: 'Incident History', icon: 'ClockIcon' },
];

const CHANNELS = ['in_app', 'email', 'sms', 'webhook', 'slack'];
const METRICS = [
  'response_time_ms', 'uptime_pct', 'booking_success_rate', 'booking_failure_rate',
  'sla_compliance_pct', 'rate_limit_pct', 'cancellation_rate', 'refund_rate',
  'revenue_today', 'commission_earned', 'conversion_rate',
];
const OPERATORS = ['>', '<', '>=', '<=', '=', '!='];
const SEVERITIES = ['info', 'warning', 'critical', 'emergency'];

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Mock Alert Generator ─────────────────────────────────────

function generateMockAlerts(): Alert[] {
  const now = new Date();
  return [
    {
      id: 'alert-1', alert_key: 'viator_response_time_high', supplier_slug: 'viator',
      title: 'Viator API Response Time Elevated', description: 'Response time exceeded 800ms threshold for 3 consecutive checks',
      severity: 'warning', status: 'active', triggered_at: new Date(now.getTime() - 12 * 60000).toISOString(),
      acknowledged_at: null, resolved_at: null, escalation_level: 0, metadata: { response_time_ms: 847, threshold: 800 },
    },
    {
      id: 'alert-2', alert_key: 'g_adventures_sync_lag', supplier_slug: 'g-adventures',
      title: 'G Adventures Sync Lag Detected', description: 'Last successful sync was 3.5 hours ago, exceeding 2-hour threshold',
      severity: 'warning', status: 'acknowledged', triggered_at: new Date(now.getTime() - 45 * 60000).toISOString(),
      acknowledged_at: new Date(now.getTime() - 30 * 60000).toISOString(), resolved_at: null, escalation_level: 0,
      metadata: { last_sync_hours: 3.5, threshold_hours: 2 },
    },
    {
      id: 'alert-3', alert_key: 'booking_success_rate_critical', supplier_slug: null,
      title: 'Platform Booking Success Rate Below Threshold', description: 'Overall booking success rate dropped to 91.2%, below 95% SLA target',
      severity: 'critical', status: 'active', triggered_at: new Date(now.getTime() - 8 * 60000).toISOString(),
      acknowledged_at: null, resolved_at: null, escalation_level: 1, metadata: { success_rate: 91.2, threshold: 95 },
    },
    {
      id: 'alert-4', alert_key: 'rate_limit_warning_viator', supplier_slug: 'viator',
      title: 'Viator Rate Limit at 82%', description: 'API rate limit consumption at 82%, approaching critical threshold',
      severity: 'warning', status: 'active', triggered_at: new Date(now.getTime() - 5 * 60000).toISOString(),
      acknowledged_at: null, resolved_at: null, escalation_level: 0, metadata: { rate_limit_pct: 82, threshold: 80 },
    },
    {
      id: 'alert-5', alert_key: 'webhook_degraded_crystal', supplier_slug: 'crystal-cruises',
      title: 'Crystal Cruises Webhook Degraded', description: 'Webhook delivery success rate dropped to 78% in the last hour',
      severity: 'critical', status: 'active', triggered_at: new Date(now.getTime() - 22 * 60000).toISOString(),
      acknowledged_at: null, resolved_at: null, escalation_level: 2, metadata: { delivery_rate: 78, threshold: 95 },
    },
    {
      id: 'alert-6', alert_key: 'sla_breach_amadeus', supplier_slug: 'amadeus',
      title: 'Amadeus SLA Breach Resolved', description: 'Response time SLA breach has been resolved. System operating normally.',
      severity: 'info', status: 'resolved', triggered_at: new Date(now.getTime() - 180 * 60000).toISOString(),
      acknowledged_at: new Date(now.getTime() - 170 * 60000).toISOString(),
      resolved_at: new Date(now.getTime() - 120 * 60000).toISOString(), escalation_level: 1,
      metadata: { resolution: 'API endpoint restored after maintenance' },
    },
  ];
}

// ─── Rule Form ────────────────────────────────────────────────

function RuleForm({ rule, onSave, onCancel }: { rule?: AlertRule | null; onSave: (data: any) => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    supplier_slug: rule?.supplier_slug || '',
    metric_name: rule?.metric_name || 'response_time_ms',
    condition_operator: rule?.condition_operator || '>',
    threshold_value: rule?.threshold_value || 1000,
    severity: rule?.severity || 'warning',
    enabled: rule?.enabled !== false,
    cooldown_minutes: rule?.cooldown_minutes || 15,
    escalation_minutes: rule?.escalation_minutes || 30,
    notification_channels: rule?.notification_channels || ['in_app'],
  });

  const toggleChannel = (ch: string) => {
    setForm((f) => ({
      ...f,
      notification_channels: f.notification_channels.includes(ch)
        ? f.notification_channels.filter((c) => c !== ch)
        : [...f.notification_channels, ch],
    }));
  };

  return (
    <div className="bg-surface-2 border border-border rounded-xl p-6 space-y-4">
      <h3 className="text-lg font-semibold text-foreground">{rule ? 'Edit Alert Rule' : 'Create Alert Rule'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Rule Name *</label>
          <input className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. High Response Time Alert" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Supplier (optional)</label>
          <input className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            value={form.supplier_slug} onChange={(e) => setForm((f) => ({ ...f, supplier_slug: e.target.value }))} placeholder="Leave blank for all suppliers" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-muted-foreground mb-1">Description</label>
          <input className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe when this alert fires" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Metric</label>
          <select className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            value={form.metric_name} onChange={(e) => setForm((f) => ({ ...f, metric_name: e.target.value }))}>
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Operator</label>
            <select className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
              value={form.condition_operator} onChange={(e) => setForm((f) => ({ ...f, condition_operator: e.target.value }))}>
              {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Threshold</label>
            <input type="number" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
              value={form.threshold_value} onChange={(e) => setForm((f) => ({ ...f, threshold_value: parseFloat(e.target.value) }))} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Severity</label>
          <select className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as any }))}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Cooldown (min)</label>
            <input type="number" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
              value={form.cooldown_minutes} onChange={(e) => setForm((f) => ({ ...f, cooldown_minutes: parseInt(e.target.value) }))} />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Escalation (min)</label>
            <input type="number" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
              value={form.escalation_minutes} onChange={(e) => setForm((f) => ({ ...f, escalation_minutes: parseInt(e.target.value) }))} />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-muted-foreground mb-2">Notification Channels</label>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((ch) => (
              <button key={ch} onClick={() => toggleChannel(ch)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.notification_channels.includes(ch) ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-3 border-border text-muted-foreground hover:border-primary/50'}`}>
                {ch.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
            className={`relative w-10 h-5 rounded-full transition-colors ${form.enabled ? 'bg-emerald-500' : 'bg-surface-4'}`}>
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-sm text-foreground">Rule {form.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          {rule ? 'Update Rule' : 'Create Rule'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-medium hover:bg-surface-4 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────

export default function EnterpriseAlertsDashboardContent() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summary, setSummary] = useState<AlertSummary>({ total: 0, active: 0, critical: 0, acknowledged: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, alertsRes] = await Promise.all([
        fetch('/api/enterprise-alerts?type=rules'),
        fetch('/api/enterprise-alerts?type=alerts&limit=200'),
      ]);
      const rulesData = await rulesRes.json();
      const alertsData = await alertsRes.json();
      setRules(rulesData.rules || []);
      // Use mock alerts if DB is empty
      const dbAlerts = alertsData.alerts || [];
      const displayAlerts = dbAlerts.length > 0 ? dbAlerts : generateMockAlerts();
      setAlerts(displayAlerts);
      setSummary(alertsData.summary || {
        total: displayAlerts.length,
        active: displayAlerts.filter((a: Alert) => a.status === 'active').length,
        critical: displayAlerts.filter((a: Alert) => a.severity === 'critical' || a.severity === 'emergency').length,
        acknowledged: displayAlerts.filter((a: Alert) => a.status === 'acknowledged').length,
        resolved: displayAlerts.filter((a: Alert) => a.status === 'resolved').length,
      });
    } catch {
      const mock = generateMockAlerts();
      setAlerts(mock);
      setSummary({ total: mock.length, active: mock.filter((a) => a.status === 'active').length, critical: mock.filter((a) => a.severity === 'critical').length, acknowledged: mock.filter((a) => a.status === 'acknowledged').length, resolved: mock.filter((a) => a.status === 'resolved').length });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAlertAction = async (alertId: string, action: string) => {
    setActionLoading(alertId + action);
    try {
      await fetch('/api/enterprise-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: `${action}_alert`, alert_id: alertId }),
      });
      setAlerts((prev) => prev.map((a) => {
        if (a.id !== alertId) return a;
        const now = new Date().toISOString();
        if (action === 'acknowledge') return { ...a, status: 'acknowledged', acknowledged_at: now };
        if (action === 'resolve') return { ...a, status: 'resolved', resolved_at: now };
        if (action === 'suppress') return { ...a, status: 'suppressed' };
        return a;
      }));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveRule = async (formData: any) => {
    const action = editingRule ? 'update_rule' : 'create_rule';
    const body = editingRule ? { action, id: editingRule.id, ...formData } : { action, ...formData };
    const res = await fetch('/api/enterprise-alerts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.success) {
      if (editingRule) {
        setRules((prev) => prev.map((r) => (r.id === editingRule.id ? data.rule : r)));
      } else {
        setRules((prev) => [data.rule, ...prev]);
      }
      setShowRuleForm(false);
      setEditingRule(null);
    }
  };

  const handleDeleteRule = async (id: string) => {
    await fetch('/api/enterprise-alerts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete_rule', id }),
    });
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const filteredAlerts = alerts.filter((a) => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterSeverity !== 'all' && a.severity !== filterSeverity) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-surface-1 border-b border-border px-6 py-5">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center">
                <Icon name="BellAlertIcon" className="w-5 h-5 text-red-400" />
              </span>
              Enterprise Alerts Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Configurable alert rules, escalation workflows, and multi-channel notifications</p>
          </div>
          <div className="flex items-center gap-3">
            {summary.active > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 text-red-400 rounded-lg text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                {summary.active} Active Alert{summary.active !== 1 ? 's' : ''}
              </span>
            )}
            <button onClick={loadData} className="px-4 py-2 bg-surface-2 border border-border rounded-lg text-sm text-foreground hover:bg-surface-3 transition-colors flex items-center gap-2">
              <Icon name="ArrowPathIcon" className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="max-w-screen-2xl mx-auto px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total Alerts', value: summary.total, color: 'text-foreground', bg: 'bg-surface-2' },
            { label: 'Active', value: summary.active, color: 'text-red-400', bg: 'bg-red-500/10' },
            { label: 'Critical', value: summary.critical, color: 'text-rose-400', bg: 'bg-rose-500/10' },
            { label: 'Acknowledged', value: summary.acknowledged, color: 'text-amber-400', bg: 'bg-amber-500/10' },
            { label: 'Resolved', value: summary.resolved, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          ].map((kpi) => (
            <div key={kpi.label} className={`${kpi.bg} border border-border rounded-xl p-4 text-center`}>
              <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface-2 rounded-xl p-1 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-surface-1 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon name={tab.icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Dashboard Tab ── */}
        {activeTab === 'dashboard' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <span className="text-sm text-muted-foreground">Filter:</span>
              <div className="flex gap-1">
                {['all', 'active', 'acknowledged', 'resolved', 'suppressed'].map((s) => (
                  <button key={s} onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-surface-2 text-muted-foreground border border-border hover:border-primary/30'}`}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {['all', 'info', 'warning', 'critical', 'emergency'].map((s) => (
                  <button key={s} onClick={() => setFilterSeverity(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterSeverity === s ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-surface-2 text-muted-foreground border border-border hover:border-primary/30'}`}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading alerts...</div>
            ) : filteredAlerts.length === 0 ? (
              <div className="text-center py-12 bg-surface-2 rounded-xl border border-border">
                <Icon name="CheckCircleIcon" className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-foreground font-medium">No alerts match your filters</p>
                <p className="text-sm text-muted-foreground mt-1">All systems operating normally</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAlerts.map((alert) => {
                  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
                  const sta = STATUS_CONFIG[alert.status] || STATUS_CONFIG.active;
                  return (
                    <div key={alert.id} className={`bg-surface-2 border rounded-xl p-4 ${alert.status === 'active' && (alert.severity === 'critical' || alert.severity === 'emergency') ? 'border-red-500/40' : 'border-border'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <span className={`w-8 h-8 rounded-lg ${sev.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <Icon name={sev.icon} className={`w-4 h-4 ${sev.color}`} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-foreground text-sm">{alert.title}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sev.bg} ${sev.color}`}>{sev.label}</span>
                              <span className={`text-xs font-medium ${sta.color}`}>{sta.label}</span>
                              {alert.escalation_level > 0 && (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-violet-500/15 text-violet-400">
                                  Escalated L{alert.escalation_level}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{alert.description}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              {alert.supplier_slug && <span className="bg-surface-3 px-2 py-0.5 rounded">{alert.supplier_slug}</span>}
                              <span>Triggered {timeAgo(alert.triggered_at)}</span>
                              {alert.acknowledged_at && <span>Ack {timeAgo(alert.acknowledged_at)}</span>}
                              {alert.resolved_at && <span className="text-emerald-400">Resolved {timeAgo(alert.resolved_at)}</span>}
                            </div>
                          </div>
                        </div>
                        {alert.status === 'active' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => handleAlertAction(alert.id, 'acknowledge')}
                              disabled={actionLoading === alert.id + 'acknowledge'}
                              className="px-3 py-1.5 bg-amber-500/15 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/25 transition-colors">
                              Acknowledge
                            </button>
                            <button onClick={() => handleAlertAction(alert.id, 'resolve')}
                              disabled={actionLoading === alert.id + 'resolve'}
                              className="px-3 py-1.5 bg-emerald-500/15 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/25 transition-colors">
                              Resolve
                            </button>
                            <button onClick={() => handleAlertAction(alert.id, 'suppress')}
                              disabled={actionLoading === alert.id + 'suppress'}
                              className="px-3 py-1.5 bg-surface-3 text-muted-foreground rounded-lg text-xs font-medium hover:bg-surface-4 transition-colors">
                              Suppress
                            </button>
                          </div>
                        )}
                        {alert.status === 'acknowledged' && (
                          <button onClick={() => handleAlertAction(alert.id, 'resolve')}
                            className="px-3 py-1.5 bg-emerald-500/15 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/25 transition-colors flex-shrink-0">
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Rules Tab ── */}
        {activeTab === 'rules' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{rules.length} alert rule{rules.length !== 1 ? 's' : ''} configured</p>
              <button onClick={() => { setEditingRule(null); setShowRuleForm(true); }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
                <Icon name="PlusIcon" className="w-4 h-4" />
                New Rule
              </button>
            </div>

            {showRuleForm && (
              <RuleForm rule={editingRule} onSave={handleSaveRule} onCancel={() => { setShowRuleForm(false); setEditingRule(null); }} />
            )}

            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading rules...</div>
            ) : rules.length === 0 ? (
              <div className="text-center py-12 bg-surface-2 rounded-xl border border-border">
                <Icon name="AdjustmentsHorizontalIcon" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium">No alert rules configured</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first alert rule to start monitoring</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rules.map((rule) => {
                  const sev = SEVERITY_CONFIG[rule.severity] || SEVERITY_CONFIG.warning;
                  return (
                    <div key={rule.id} className="bg-surface-2 border border-border rounded-xl p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground text-sm">{rule.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sev.bg} ${sev.color}`}>{sev.label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rule.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-surface-3 text-muted-foreground'}`}>
                              {rule.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          {rule.description && <p className="text-xs text-muted-foreground mt-1">{rule.description}</p>}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="bg-surface-3 px-2 py-0.5 rounded font-mono">{rule.metric_name} {rule.condition_operator} {rule.threshold_value}</span>
                            {rule.supplier_slug && <span className="bg-surface-3 px-2 py-0.5 rounded">{rule.supplier_slug}</span>}
                            <span>Cooldown: {rule.cooldown_minutes}m</span>
                            <span>Escalate: {rule.escalation_minutes}m</span>
                            <span>Channels: {rule.notification_channels.join(', ')}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => { setEditingRule(rule); setShowRuleForm(true); }}
                            className="px-3 py-1.5 bg-surface-3 text-foreground rounded-lg text-xs font-medium hover:bg-surface-4 transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)}
                            className="px-3 py-1.5 bg-red-500/15 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/25 transition-colors">
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Escalations Tab ── */}
        {activeTab === 'escalations' && (
          <div className="space-y-4">
            <div className="bg-surface-2 border border-border rounded-xl p-5">
              <h3 className="font-semibold text-foreground mb-4">Escalation Workflow</h3>
              <div className="space-y-3">
                {[
                  { level: 'L1', time: '0–15 min', action: 'In-app notification to on-call engineer', color: 'bg-blue-500/15 text-blue-400' },
                  { level: 'L2', time: '15–30 min', action: 'Email notification to team lead + SMS to on-call', color: 'bg-amber-500/15 text-amber-400' },
                  { level: 'L3', time: '30–60 min', action: 'SMS + Email to engineering manager', color: 'bg-orange-500/15 text-orange-400' },
                  { level: 'L4', time: '60+ min', action: 'Emergency escalation to CTO + all channels', color: 'bg-red-500/15 text-red-400' },
                ].map((step) => (
                  <div key={step.level} className="flex items-center gap-4 p-3 bg-surface-3 rounded-lg">
                    <span className={`w-10 h-10 rounded-lg ${step.color} flex items-center justify-center text-sm font-bold flex-shrink-0`}>{step.level}</span>
                    <div>
                      <div className="text-sm font-medium text-foreground">{step.action}</div>
                      <div className="text-xs text-muted-foreground">{step.time} after alert trigger</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface-2 border border-border rounded-xl p-5">
              <h3 className="font-semibold text-foreground mb-4">Active Escalations</h3>
              {alerts.filter((a) => a.escalation_level > 0 && a.status !== 'resolved').length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Icon name="CheckCircleIcon" className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm">No active escalations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.filter((a) => a.escalation_level > 0 && a.status !== 'resolved').map((alert) => {
                    const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
                    return (
                      <div key={alert.id} className="flex items-center justify-between p-3 bg-surface-3 rounded-lg">
                        <div>
                          <div className="text-sm font-medium text-foreground">{alert.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">Level {alert.escalation_level} escalation · {timeAgo(alert.triggered_at)}</div>
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${sev.bg} ${sev.color}`}>{sev.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Notifications Tab ── */}
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { channel: 'Email', icon: 'EnvelopeIcon', status: 'Configured', color: 'text-blue-400', bg: 'bg-blue-500/15', desc: 'SMTP via platform email service' },
                { channel: 'SMS', icon: 'DevicePhoneMobileIcon', status: 'Available', color: 'text-emerald-400', bg: 'bg-emerald-500/15', desc: 'Twilio SMS integration' },
                { channel: 'In-App', icon: 'BellIcon', status: 'Active', color: 'text-violet-400', bg: 'bg-violet-500/15', desc: 'Real-time in-app notifications' },
              ].map((ch) => (
                <div key={ch.channel} className="bg-surface-2 border border-border rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-9 h-9 rounded-lg ${ch.bg} flex items-center justify-center`}>
                      <Icon name={ch.icon} className={`w-5 h-5 ${ch.color}`} />
                    </span>
                    <div>
                      <div className="font-semibold text-foreground text-sm">{ch.channel}</div>
                      <div className={`text-xs ${ch.color}`}>{ch.status}</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{ch.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-surface-2 border border-border rounded-xl p-5">
              <h3 className="font-semibold text-foreground mb-4">Recent Notifications</h3>
              <div className="space-y-2">
                {alerts.slice(0, 6).map((alert) => {
                  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
                  return (
                    <div key={alert.id} className="flex items-center justify-between p-3 bg-surface-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${sev.dot}`} />
                        <div>
                          <div className="text-sm text-foreground">{alert.title}</div>
                          <div className="text-xs text-muted-foreground">{timeAgo(alert.triggered_at)} · in_app{alert.severity === 'critical' ? ', email, sms' : ', email'}</div>
                        </div>
                      </div>
                      <span className={`text-xs ${sev.color}`}>{sev.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Incident History Tab ── */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="bg-surface-2 border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border">
                <h3 className="font-semibold text-foreground">Incident History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Alert</th>
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Severity</th>
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Supplier</th>
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Triggered</th>
                      <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map((alert) => {
                      const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
                      const sta = STATUS_CONFIG[alert.status] || STATUS_CONFIG.active;
                      const duration = alert.resolved_at
                        ? Math.round((new Date(alert.resolved_at).getTime() - new Date(alert.triggered_at).getTime()) / 60000)
                        : null;
                      return (
                        <tr key={alert.id} className="border-b border-border/50 hover:bg-surface-3/50 transition-colors">
                          <td className="px-4 py-3 text-foreground font-medium">{alert.title}</td>
                          <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${sev.bg} ${sev.color}`}>{sev.label}</span></td>
                          <td className="px-4 py-3 text-muted-foreground">{alert.supplier_slug || 'Platform'}</td>
                          <td className="px-4 py-3"><span className={`text-xs font-medium ${sta.color}`}>{sta.label}</span></td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{timeAgo(alert.triggered_at)}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{duration ? `${duration}m` : 'Ongoing'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
